// /config/search-config.js
import { ChangeCategories, ChangeTypes } from "./auditLogConfig";
import { MID_APP_STATUS } from "../utilities/statusTones";
import { ALL_TIER_OPTIONS, COMPANY_STATUS_OPTIONS } from "../constants/companies";

export const SEARCH_CONFIG = {
  // Audit Log section
  auditLog: {
    fields: [
      {
        key: "changeCategory",
        type: "string",
        ui: "select",
        default: "",
        apiName: "changeCategory",
        label: "Category",
        options: Object.values(ChangeCategories),
      },
      {
        key: "changeType",
        type: "string",
        ui: "select",
        default: "",
        apiName: "changeType",
        label: "Type",
        options: Object.values(ChangeTypes),
      },
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
    ],
  },

  // Approvals section
  approvals: {
    fields: [
      // UI/date
      {
        key: "startDate",
        type: "string",
        ui: "date",
        default: "",
        apiName: "startDate",
      },
      {
        key: "endDate",
        type: "string",
        ui: "date",
        default: "",
        apiName: "endDate",
      },

      // // Multiselects (declare where options come from + how to send to API)
      // {
      //   key: "campaigns",
      //   type: "multi",
      //   default: [],
      //   optionsProp: "campaignOptions",
      //   apiName: "campaigns",
      //   encode: "array",
      // }, // or "csv"

      {
        key: "cardBin",
        type: "string",
        ui: "text",
        default: "",
        apiName: "cardBin",
      },
      {
        key: "campaignName",
        type: "string",
        ui: "text",
        default: "",
        apiName: "campaignName",
      },
    ],
  },

  // Analysis section (adds statistical significance threshold)
  analysis: {
    fields: [
      // UI/date
      {
        key: "startDate",
        type: "string",
        ui: "date",
        default: "",
        apiName: "startDate",
      },
      {
        key: "endDate",
        type: "string",
        ui: "date",
        default: "",
        apiName: "endDate",
      },
      {
        key: "country",
        type: "string",
        ui: "text",
        default: "",
        apiName: "country",
      },
      {
        key: "transactionType",
        type: "string",
        ui: "text",
        default: "",
        apiName: "transactionType",
      },

      // Multiselects (declare where options come from + how to send to API)
      // {
      //   key: "campaigns",
      //   type: "multi",
      //   default: [],
      //   optionsProp: "campaignOptions",
      //   apiName: "campaigns",
      //   encode: "array",
      // }, // or "csv"

      // Numbers
      { key: "minTx", type: "number", default: 50, apiName: "minTx" },
      { key: "minGap", type: "number", default: 5, apiName: "minGap" },

      // Boolean (as 0/1 for API)
      {
        key: "isCIT",
        type: "boolean",
        default: false,
        apiName: "isCIT",
        encode: "boolInt",
      },
    ],
  },

  // Companies section
  companies: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "companyType",
        type: "string",
        ui: "select",
        default: "",
        apiName: "companyType",
        label: "Type",
        // NOTE: backend supports these core values. "parent" is a computed
        // attribute (client with children) rather than a stored type, so we
        // do not expose it here. See SOUND-292 for discussion.
        options: ["internal", "client", "partner"],
      },
      {
        // New — source of the company (SOUND-291). Filters by source.type.
        key: "source",
        type: "string",
        ui: "select",
        default: "",
        apiName: "source",
        label: "Source",
        options: ["partner", "self-serve", "internal"],
      },
      {
        // Specific partner filter. Only meaningful when source=partner.
        // Uses a company picker UI (see CompanyPickerField). Applied
        // server-side (SOUND-295) via companies.sources[].partnerId.
        key: "partnerId",
        type: "string",
        ui: "companyPicker",
        default: "",
        apiName: "partnerId",
        label: "Partner",
        pickerCompanyType: "partner",
        // Only visible when source === "partner"
        visibleWhen: (draft) => draft.source === "partner",
      },
      {
        // Filter by the company's application status (SOUND-276). Applied
        // server-side (SOUND-295) — resolves matching companyIds from the
        // applications collection, then constrains the company query.
        key: "applicationStatus",
        type: "string",
        ui: "select",
        default: "",
        apiName: "applicationStatus",
        label: "Application Status",
        options: ["new", "in_progress", "submitted", "approved", "rejected", "needs_info"],
      },
      {
        // Renamed in UI (was "Status"): this is the company account status.
        // Moved from table column to filter drawer per SOUND-292.
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Account Status",
        options: COMPANY_STATUS_OPTIONS,
      },
      {
        // The filter drawer spans company types, so it is the one surface that
        // legitimately offers the union. Create and edit controls must narrow
        // by type via `tierOptionsFor()` (SOUND-296).
        key: "tier",
        type: "string",
        ui: "select",
        default: "",
        apiName: "tier",
        label: "Tier",
        options: ALL_TIER_OPTIONS,
      },
    ],
  },

  // Applications section
  applications: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Status",
        options: ["new", "in_progress", "submitted", "approved", "rejected", "needs_info"],
      },
    ],
  },

  // MIDs section
  mids: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Status",
        options: ["active", "suspended", "closed"],
      },
      {
        // SOUND-688 — the pended STATE. `pended` stopped being a status, so
        // "everything with an outstanding ask" is no longer reachable from the
        // Status dropdown. Boolean-shaped, so the only meaningful value is
        // "true"; unset means "don't filter", NOT "has no pends".
        //
        // Driven inline by the toggle on the list, so MidApplications hides it
        // from the SearchPanel drawer — same treatment as the ticket inbox's
        // assigneeId. It still counts as an active filter, because it narrows.
        key: "hasPends",
        type: "string",
        ui: "select",
        default: "",
        apiName: "hasPends",
        label: "Has open pends",
        options: ["true"],
      },
      {
        key: "source",
        type: "string",
        ui: "select",
        default: "",
        apiName: "source",
        label: "Source",
        options: ["partner", "self-serve", "internal"],
      },
      {
        key: "companyId",
        type: "string",
        ui: "companyPicker",
        default: "",
        apiName: "companyId",
        label: "Client",
        pickerCompanyType: "client",
      },
      {
        key: "dbaName",
        type: "string",
        ui: "text",
        default: "",
        apiName: "dbaName",
        label: "DBA",
      },
      {
        key: "processorName",
        type: "string",
        ui: "text",
        default: "",
        apiName: "processorName",
        label: "Processor",
      },
    ],
  },

  // MID Applications section
  midApplications: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Status",
        // Derived, not listed. A hardcoded copy here went stale when the
        // vocabulary gained out_for_signature / submitted / underwriting —
        // 109 applications became unfilterable from the search panel.
        options: Object.keys(MID_APP_STATUS),
      },
      {
        // SOUND-688 — the pended STATE. `pended` stopped being a status, so
        // "everything with an outstanding ask" is no longer reachable from the
        // Status dropdown. Boolean-shaped, so the only meaningful value is
        // "true"; unset means "don't filter", NOT "has no pends".
        //
        // Driven inline by the toggle on the list, so MidApplications hides it
        // from the SearchPanel drawer — same treatment as the ticket inbox's
        // assigneeId. It still counts as an active filter, because it narrows.
        key: "hasPends",
        type: "string",
        ui: "select",
        default: "",
        apiName: "hasPends",
        label: "Has open pends",
        options: ["true"],
      },
      {
        key: "source",
        type: "string",
        ui: "select",
        default: "",
        apiName: "source",
        label: "Source",
        options: ["partner", "self-serve", "internal"],
      },
      {
        key: "companyId",
        type: "string",
        ui: "companyPicker",
        default: "",
        apiName: "companyId",
        label: "Client",
        pickerCompanyType: "client",
      },
      {
        key: "dbaName",
        type: "string",
        ui: "text",
        default: "",
        apiName: "dbaName",
        label: "DBA",
      },
      {
        key: "processorName",
        type: "string",
        ui: "text",
        default: "",
        apiName: "processorName",
        label: "Processor",
      },
    ],
  },

  // Tickets section (SOUND-493). Inline controls on the list drive the common
  // params (search / state / status); the SearchPanel drawer exposes the fuller
  // internal set. `assigneeId` stays a persisted field so "Assigned to me"
  // round-trips in the URL, but it's hidden from the drawer (driven inline).
  tickets: {
    // NOTE: SearchPanel renders drawer fields in THIS array's order. Client and
    // Request Type lead because they are the two filters ops reaches for most.
    // Reordering is safe — every consumer (usePersistedSearch's read/write/reset)
    // iterates the array without depending on position.
    fields: [
      {
        key: "companyId",
        type: "string",
        ui: "companyPicker",
        default: "",
        apiName: "companyId",
        label: "Client",
        pickerCompanyType: "client",
      },
      {
        // Options are NOT hardcoded here — the 34-key vocabulary is
        // audience-filtered server-side and served by GET /tickets/request-types,
        // so Tickets.jsx injects it via `extraOptions.requestTypeOptions`.
        // Hardcoding would drift from config/ticketRequestTypes.mjs and leak
        // internal-audience types to client users.
        key: "requestType",
        type: "string",
        ui: "select",
        default: "",
        apiName: "requestType",
        label: "Request Type",
        optionsProp: "requestTypeOptions",
      },
      {
        // SOUND-785 — workflow variant. Options come from the templates
        // (internal-only GET /ticket-templates), injected by Tickets.jsx as
        // `variantOptions`; the drawer hides it from clients and partners.
        // `variantKey` survives new template versions, which is why the filter
        // keys on it rather than on templateId.
        key: "variantKey",
        type: "string",
        ui: "select",
        default: "",
        apiName: "variantKey",
        label: "Variant",
        optionsProp: "variantOptions",
      },
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        // Defaults to OPEN: the full 375-ticket list is too noisy as a landing
        // view (only 88 are open).
        //
        // "all" is an EXPLICIT value, not the empty string, because
        // usePersistedSearch.write() deletes empty values from the URL and
        // decode() then falls back to `default` — so an empty "All" would spring
        // straight back to "open" and the user could never see every ticket.
        // The server treats any state other than open/closed as no filter, so
        // "all" is a no-op server-side (buildTicketFilter).
        //
        // `omitBlankOption` suppresses the drawer's implicit blank "All" entry,
        // which would otherwise be a second, broken "All".
        key: "state",
        type: "string",
        ui: "select",
        default: "open",
        apiName: "state",
        label: "State",
        options: ["open", "closed", "all"],
        omitBlankOption: true,
      },
      {
        key: "statusCategory",
        type: "string",
        ui: "select",
        default: "",
        apiName: "statusCategory",
        label: "Category",
        options: ["todo", "in_progress", "done", "canceled"],
      },
      {
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Status",
        options: [
          "open",
          "in_progress",
          "waiting",
          "resolved",
          "declined",
          "withdrawn",
          "canceled",
        ],
      },
      {
        key: "priority",
        type: "string",
        ui: "select",
        default: "",
        apiName: "priority",
        label: "Priority",
        options: ["low", "normal", "high", "urgent"],
      },
      {
        key: "kind",
        type: "string",
        ui: "select",
        default: "",
        apiName: "kind",
        label: "Kind",
        options: ["ticket", "task"],
      },
      {
        key: "assigneeId",
        type: "string",
        ui: "text",
        default: "",
        apiName: "assigneeId",
        label: "Assignee",
      },
      // SLA view (SOUND-535) — driven by the inline controls, hidden from the
      // drawer (like assigneeId). Persisted so the SERVER-side SLA sort/filter
      // round-trips in the URL. `slaBucket` → server bucket filter (cross-page);
      // `sort=sla` → breached-first, soonest-due server sort.
      {
        key: "slaBucket",
        type: "string",
        ui: "select",
        default: "",
        apiName: "slaBucket",
        label: "SLA",
        options: ["breached", "approaching", "on_track", "none"],
      },
      {
        key: "sort",
        type: "string",
        ui: "select",
        default: "",
        apiName: "sort",
        label: "Sort",
        options: ["sla"],
      },
      // SOUND-501 — Unassigned queue. Driven by the inline toggle + the
      // Unassigned KPI, hidden from the drawer (like assigneeId/slaBucket/sort).
      // `assigned=none` → server-side {assignee: null} list filter (added in 535).
      {
        key: "assigned",
        type: "string",
        ui: "select",
        default: "",
        apiName: "assigned",
        label: "Assigned",
        options: ["none", "any"],
      },
    ],
  },

  // Disputifier section
  disputifier: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "program",
        type: "string",
        ui: "select",
        default: "",
        apiName: "program",
        label: "Program",
        options: ["rdr", "cdrn", "ethoca", "tc40"],
      },
    ],
  },

  // Users section
  users: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        key: "userType",
        type: "string",
        ui: "select",
        default: "",
        apiName: "userType",
        label: "User Type",
        options: ["internal", "client", "partner"],
      },
      {
        key: "role",
        type: "string",
        ui: "select",
        default: "",
        apiName: "role",
        label: "Role",
        options: ["super", "admin", "member"],
      },
      {
        key: "status",
        type: "string",
        ui: "select",
        default: "",
        apiName: "status",
        label: "Status",
        options: ["active", "inactive"],
      },
    ],
  },

  // Rules section
  rules: {
    fields: [
      {
        key: "bin",
        type: "string",
        ui: "text",
        default: "",
        apiName: "bin",
      },
      {
        key: "campaign",
        type: "string",
        ui: "text",
        default: "",
        apiName: "campaign",
      },
      {
        key: "category",
        type: "string",
        ui: "text",
        default: "",
        apiName: "category",
      },
      {
        key: "country",
        type: "string",
        ui: "text",
        default: "",
        apiName: "country",
      },
    ],
  },

  // SOUND-661 — files. Replaces FilesTab's hardcoded 8-category quick-toggle
  // row, which could not reach 1,426 of the 3,421 files in prod (41.7%) because
  // the vocabulary had grown to 157 distinct values and the toggles never did.
  files: {
    fields: [
      {
        key: "search",
        type: "string",
        ui: "text",
        default: "",
        apiName: "search",
        label: "Search",
      },
      {
        // No `options` here on purpose. The vocabulary is SERVER-owned — the
        // caller injects it via SearchPanel's `extraOptions`, the same seam
        // ticket request types use. Hardcoding it here is the exact bug being
        // fixed, one layer down.
        key: "category",
        type: "string",
        ui: "select",
        default: "",
        apiName: "category",
        label: "Category",
        optionsProp: "categoryOptions",
      },
      {
        key: "companyId",
        type: "string",
        ui: "companyPicker",
        default: "",
        apiName: "companyId",
        label: "Client",
        pickerCompanyType: "client",
      },
    ],
  },
};

// ─── Company-scoped variants (SOUND-645) ────────────────────────────────────
//
// The company record's MIDs / Applications / Tickets tabs are the same lists
// with the company already decided. They get their OWN namespaces for two
// reasons:
//
//   1. Persistence is per-namespace. Sharing "mids" would carry a filter set on
//      /mids into a company's tab and back again, so a stale client filter could
//      silently contradict the record you are looking at.
//   2. companyId is FORCED by the tab, not chosen by the user, so the field is
//      dropped here — leaving it would render a client picker inside a page that
//      is already about one client, and let a filter override the scope.
//
// DERIVED, not copied. Add a filter to `mids` and its tab gets it too; the two
// cannot drift, which is the failure mode a duplicated field list invites.
function scopedTo(namespace, omit = ["companyId"]) {
  const base = SEARCH_CONFIG[namespace];
  if (!base) throw new Error(`scopedTo: unknown namespace "${namespace}"`);
  return { ...base, fields: base.fields.filter((f) => !omit.includes(f.key)) };
}

SEARCH_CONFIG.companyMids = scopedTo("mids");
SEARCH_CONFIG.companyMidApplications = scopedTo("midApplications");
// Tickets also drops `assigned`/`assigneeId`-style view controls? No — those are
// still useful inside a company's queue. Only the company itself is forced.
SEARCH_CONFIG.companyTickets = scopedTo("tickets");
SEARCH_CONFIG.companyFiles = scopedTo("files");
